import { Component, ElementRef, inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core'
import { AsyncPipe, CommonModule, DatePipe } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { AlertController, IonContent, IonicModule, LoadingController, ModalController } from '@ionic/angular'
import { firstValueFrom, Observable } from 'rxjs'
import { Assistance } from '../../../models/assistance'
import { AssistanceJournalEntry } from '../../../models/EditorAssistance'
import { AssistanceStatusName } from '../../../models/assistanceStatus'
import { AssistanceService } from '../../../services/domain/assistance/assistance.service'
import { AssistantService } from '../../../services/domain/assistant/assistant.service'
import { AssistanceStatusManagerService } from '../../../services/assistance-status-manager.service'
import { RoleAssistantFab, RoleAssistantFabState } from './roleAssistantFab'
import { AssistantAssignment } from '../../../models/assistantAssignment'
import { UsersService } from '../../../services/domain/users.service'
import { User } from '../../../models/user'
import { DateRangeViewComponent } from '../../../components/date-range-view/date-range-view.component'
import { DateUtils } from '../../../services/date-utils.service'
import { BypassHtmlSanitizerPipe } from '../../../shared/pipes/bypass-html-sanitizer.pipe'
import { GetPrimaryContactPipe } from '../../../shared/pipes/get-primary-contact.pipe'
import { TagEditorComponent } from '../../event/components/tag-editor/tag-editor.component'
import { AssistanceCareLogEditorComponent } from '../../assistance/assistance-care-log-editor/assistance-care-log-editor.component'
import { AssistanceAttachmentsComponent } from '../../assistance/assistance-attachments/assistance-attachments.component'
import { CareLogEditorAssistant } from '../../../models/assistance-care-log.model'
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'
import { FeatureFlags } from '../../../shared/featureFlags'
import { ConfigService } from '../../../services/config.service'
import { ConfigKeys } from '../../config/ConfigKeys'
import { JournalEntriesHistoryService } from '../../../services/domain/journal-entries-history.service'
import { PaginatedStoreWithData } from '../../../services/feathers.service'
import { JournalEntryHistoryItem } from '../../../models/journal-entry-history-item'
import { PaginatedWithAggregates } from '../../../services/feathersPaginatedDataStore'
import { map, tap } from 'rxjs/operators'
import { ClientService } from '../../../services/domain/client/client.service'
import { PrimeNgToastService } from '../../../services/prime-ng-toast/prime-ng-toast.service'

@Component({
  selector: 'app-role-assistant-assistance-editor',
  templateUrl: './role-assistant-assistance-editor.component.html',
  styleUrls: ['./role-assistant-assistance-editor.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    DateRangeViewComponent,
    BypassHtmlSanitizerPipe,
    GetPrimaryContactPipe,
    TagEditorComponent,
    AssistanceCareLogEditorComponent,
    AssistanceAttachmentsComponent,
    TranslocoModule,
    AsyncPipe,
    DatePipe,
  ],
})
export class RoleAssistantAssistanceEditorComponent implements OnInit, OnChanges {
  @ViewChild(IonContent) private content: IonContent
  @ViewChild('tagEditorAnchor', { read: ElementRef }) private tagEditorAnchor: ElementRef

  private modalCtrl = inject(ModalController)
  private alertCtrl = inject(AlertController)
  private assistanceService = inject(AssistanceService)
  private statusManagerService = inject(AssistanceStatusManagerService)
  private toaster = inject(PrimeNgToastService)
  private usersService = inject(UsersService)
  private loading = inject(LoadingController)
  private assistantService = inject(AssistantService)
  private transloco = inject(TranslocoService)
  private configService = inject(ConfigService)
  private journalEntriesHistoryService = inject(JournalEntriesHistoryService)
  private clientService = inject(ClientService)

  @Input() assistantId: number
  @Input() assistance: Assistance

  public assignment: AssistantAssignment
  private _mappedAssistantAssignment: CareLogEditorAssistant[] = []
  myJournalEntry = ''
  savedJournalEntry = ''

  totalCareLogMinutes = 0
  savingJournalEntry = false

  private assistanceCurrentlyRunning = false
  fab = new RoleAssistantFab()
  RoleAssistantFabState = RoleAssistantFabState
  CONFIRMED: AssistanceStatusName = AssistanceStatusName.CONFIRMED
  keyCoordinator$: Observable<User>

  journalEntryHistoryEnabled$: Observable<boolean>
  private _historyStore: PaginatedStoreWithData<JournalEntryHistoryItem, never> | null = null
  journalHistoryData$: Observable<PaginatedWithAggregates<JournalEntryHistoryItem, never>> | null = null
  private currentSkip = 0

  async ngOnInit() {
    console.log('modal onInit', this.assistantId, this.assistance)
    this.journalEntryHistoryEnabled$ = this.configService.getBoolean(ConfigKeys.JOURNAL_ENTRY_HISTORY_ENABLED)
    await this.initAssistance(this.assistance)
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (changes.hasOwnProperty(propName)) {
        if (propName === 'assistance') {
          void this.initAssistance(this.assistance)
        }
      }
    }
  }

  dismissModal() {
    this.modalCtrl.dismiss()
  }

  async confirmAssistance() {
    console.log(this.assignment)

    // warning - this mutates this.assignment
    if (
      await this.statusManagerService.advanceAssignmentStatusWithGlobalValidationMutating(
        this.assistance,
        this.assignment,
        true,
      )
    ) {
      this.fab.set(RoleAssistantFabState.working)

      await this.assistanceService.patchAssignment(this.assignment)

      this.updateFab(this.assistance)
      this.toaster.displayCustomToast('success', 'Asistence byla úspěšně potvrzena.')
    }
  }

  async startAssistance() {
    this.fab.set(RoleAssistantFabState.working)

    if (!this.assistance.realizedFrom) {
      this.assistance = await this.assistanceService.patchNonRecurrently({
        id: this.assistance.id,
        realizedFrom: new Date().getTime(),
      })
      this.toaster.displayCustomToast('success', 'Asistence byla úspěšně započata.')
    }

    this.assistanceCurrentlyRunning = true
    this.fab.set(RoleAssistantFabState.assistanceReadyToEnd)
  }

  private updateFab(assistance: Assistance) {
    if (assistance.realizedFrom && assistance.realizedTo) {
      this.fab.set(RoleAssistantFabState.assistanceFinished)
      return
    }

    if (assistance.realizedFrom && !assistance.realizedTo) {
      this.assistanceCurrentlyRunning = true
      this.fab.set(RoleAssistantFabState.assistanceReadyToEnd)
      return
    }

    if (this.assignment.status.name === AssistanceStatusName.SENT_TO_ASSISTANT) {
      this.fab.set(RoleAssistantFabState.assistanceReadyToBeConfirmed)
      return
    }

    if (this.assistanceService.assistanceCanBeStartedByAssistant(assistance, this.assistantId)) {
      this.fab.set(RoleAssistantFabState.assistanceReadyToStart)
      return
    }

    this.fab.set(RoleAssistantFabState.none)
  }

  get canWriteJournalEntry(): boolean {
    if (!this.assistance || !this.assignment) return false
    return (
      !!this.assistance.realizedFrom ||
      !!this.assistance.realizedTo ||
      (this.assistance.status?.name === AssistanceStatusName.CONFIRMED &&
        this.assignment.status?.name === AssistanceStatusName.CONFIRMED)
    )
  }

  get assistanceElapsedMinutes(): number | null {
    if (!this.assistance?.realizedFrom) return null
    const endMs = this.assistance.realizedTo ? new Date(this.assistance.realizedTo).getTime() : Date.now()
    return Math.floor((endMs - new Date(this.assistance.realizedFrom).getTime()) / 60_000)
  }

  get mappedAssistantAssignment(): CareLogEditorAssistant[] {
    return this._mappedAssistantAssignment
  }

  private async enrichJournalEntriesForDisplay(assistance: Assistance): Promise<void> {
    const entries = assistance.journalEntries
    if (!entries?.length) return

    const enriched = await Promise.all(
      entries.map(async (entry: AssistanceJournalEntry): Promise<{ assistantId: number; content: string } | null> => {
        if (entry.assistantId) {
          return { assistantId: entry.assistantId, content: entry.content ?? '' }
        }
        if (entry.authorUserId) {
          const assistantId = await firstValueFrom(this.assistantService.getAssistantIdByUserId(entry.authorUserId))
          return { assistantId, content: entry.content ?? '' }
        }
        return null
      }),
    )
    assistance.journalEntries = enriched.filter(Boolean) as { assistantId: number; content: string }[]
  }

  private async initAssistance(assistance: Assistance) {
    this._historyStore = null
    this.journalHistoryData$ = null
    await this.enrichJournalEntriesForDisplay(assistance)
    this.assignment = this.assistance.assistantAssignments.getAssignmentByAssistantId(this.assistantId)
    this._mappedAssistantAssignment = this.assignment
      ? [
          {
            assignmentId: this.assignment.id,
            assistantId: this.assistantId,
            fullname: this.assignment.assistant?.fullname ?? '',
          },
        ]
      : []
    this.keyCoordinator$ = this.clientService
      .getById(this.assistance.clientId)
      .pipe(map((client) => client.keyWorkerUser))

    this.myJournalEntry = assistance.journalEntries?.find((e) => e.assistantId === this.assistantId)?.content ?? ''
    this.savedJournalEntry = this.myJournalEntry

    console.log('initAssistance', this.assignment)

    this.updateFab(assistance)
  }

  get isJournalEntryDirty(): boolean {
    return this.myJournalEntry !== this.savedJournalEntry
  }

  async scrollToTagEditor(): Promise<void> {
    if (!this.tagEditorAnchor) return
    const el = this.tagEditorAnchor.nativeElement as HTMLElement
    const scrollEl = await this.content.getScrollElement()
    const elementTop = el.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top + scrollEl.scrollTop
    const top = elementTop - (scrollEl.clientHeight - el.offsetHeight) / 2
    await this.content.scrollToPoint(0, top, 400)
  }

  onMyJournalEntryChange(content: string): void {
    this.myJournalEntry = content
  }

  onAccordionChange(event: CustomEvent): void {
    const value = event.detail.value

    if (
      !this._historyStore &&
      (value === 'journal-history' || (Array.isArray(value) && value.includes('journal-history')))
    ) {
      this.initHistory()
    }
  }

  private initHistory() {
    this._historyStore = this.journalEntriesHistoryService.createStore(this.assistance.id, this.assistance.client.id)
    this.journalHistoryData$ = this._historyStore.data$.pipe(tap((data) => (this.currentSkip = data.skip ?? 0)))
  }

  journalHistoryOlder(): void {
    if (!this._historyStore) return
    this._historyStore.store.requery({ $skip: this.currentSkip + 1 })
  }

  journalHistoryNewer(): void {
    if (!this._historyStore) return
    if (this.currentSkip <= 0) return
    this._historyStore.store.requery({ $skip: this.currentSkip - 1 })
  }

  async saveJournalEntry(): Promise<void> {
    this.savingJournalEntry = true
    try {
      const entries: AssistanceJournalEntry[] = [...(this.assistance.journalEntries ?? [])]
      const idx = entries.findIndex((e) => e.assistantId === this.assistantId)
      const entry: AssistanceJournalEntry = { assistantId: this.assistantId, content: this.myJournalEntry }
      if (idx >= 0) entries[idx] = entry
      else entries.push(entry)

      const result = await this.assistanceService.patchNonRecurrently({
        id: this.assistance.id,
        journalEntries: entries,
      })
      await this.enrichJournalEntriesForDisplay(result)
      this.assistance = result
      this.assistance.journalEntries = this.assistance.journalEntries ?? entries
      this.savedJournalEntry = this.myJournalEntry
      this.toaster.displayCustomToast(
        'success',
        this.transloco.translate('ASSISTANCE_CARE_LOG_EDITOR.TOAST_ENTRY_SAVED'),
      )
    } catch {
      this.toaster.displayCustomToast(
        'error',
        this.transloco.translate('ASSISTANCE_CARE_LOG_EDITOR.TOAST_ENTRY_SAVE_FAILED'),
      )
    } finally {
      this.savingJournalEntry = false
    }
  }

  async askEndAssistance() {
    const alert = await this.alertCtrl.create({
      header: 'Ukončit asistenci?',
      buttons: [
        {
          text: 'Zrušit',
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: 'Ano',
          cssClass: 'primary',
          role: 'confirm',
          handler: async () => {
            await this.endAssistanceWithSavingPopup()
            await alert.dismiss()
          },
        },
      ],
    })

    await alert.present()
  }

  private async endAssistanceWithSavingPopup() {
    const endTime = new Date()

    const loading = await this.loading.create({
      cssClass: 'loadingBanner',
      message: `Ukončuji asistenci s časem ${DateUtils.dateToHhColonMm(endTime)}`,
      backdropDismiss: false,
      spinner: 'dots',
    })
    await loading.present()

    await this.endAssistance(endTime)
    await loading.dismiss()
  }

  private async endAssistance(endTime: Date) {
    this.fab.set(RoleAssistantFabState.working)
    this.assistance = await this.assistanceService.patchNonRecurrently({
      id: this.assistance.id,
      realizedTo: endTime.getTime(),
    })
    this.assistanceCurrentlyRunning = false
    this.fab.set(RoleAssistantFabState.assistanceFinished)

    this.toaster.displayCustomToast('success', 'Asistence byla úspěšně ukončena.')
  }

  protected readonly FeatureFlags = FeatureFlags
}
